import { Button, message, Spin, Tag } from "antd"
import { SyncOutlined } from "@ant-design/icons"
import { useState, useEffect, useMemo } from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import apiClient from "../../Utils/apiClient"
import PanelLayout from "../../Layout/PanelLayout"
import TableBox from "../../components/TableBox/TableBox"
import HostnameLink from "../../components/HostnameLink/HostnameLink"
import DeviceModelImage from "../../components/DeviceModelImage/DeviceModelImage"
import StatusTag from "../../components/StatusTag/StatusTag"
import EmptyState from "../../components/EmptyState/EmptyState"
import { getStoredPageSize } from "../../Utils/TableUtils/TableUtils"
import { useHeaderContext } from "../../Utils/HeaderContext"
import { renderDate } from "../../Utils/utils"
import { resolveDeviceModelImage } from "../../Utils/deviceModelImage"
import AutoCompleteBox from "../../components/AutoCompleteBox/AutoCompleteBox"
import "../Operations/Operations.scss"

dayjs.extend(relativeTime)

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  const parts = text.split(/\\|\//)
  return parts[parts.length - 1] || text
}

const RebootDevices = () => {
  const {
    isDeviceRebooting,
    deviceTasks,
  } = useHeaderContext()
  const page = "Reboot Devices"
  const [filters, setFilters] = useState({
    hostname: "",
    user: "",
    deviceStatus: "",
  })

  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })

  const [loading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [statusChip, setStatusChip] = useState("")

  const isAnyDeviceRebooting = () => {
    return Object.values(deviceTasks).some(
      (task) =>
        task.command === "reboot" &&
        ["pending", "running"].includes(task.status)
    )
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  }

  const fetchDeviceData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters,
    customStatus = statusChip
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        requiresReboot: true,
        hostname: customFilters.hostname || undefined,
        user: customFilters.user || undefined,
        deviceStatus: customStatus || customFilters.deviceStatus || undefined,
      }

      const res = await apiClient("devices", "GET", null, { params })

      if (res?.data) {
        setDevices(res.data)
        setPaginationData(res.pageData)
      }
    } catch (err) {
      console.log("Error fetching devices", err)
    } finally {
      setLoading(false)
    }
  }

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    fetchDeviceData(current, pageSize)
  }

  useEffect(() => {
    fetchDeviceData(1, paginationData.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const summary = useMemo(() => {
    const online = devices.filter((d) => d.deviceStatus === "Online").length
    const away = devices.filter((d) => d.deviceStatus === "Away").length
    const offline = devices.filter((d) => d.deviceStatus === "Offline").length
    return {
      total: paginationData.total || devices.length,
      online,
      away,
      offline,
      selected: selectedRowKeys.length,
    }
  }, [devices, paginationData.total, selectedRowKeys.length])

  const handleClearFilter = () => {
    const empty = { hostname: "", user: "", deviceStatus: "" }
    setFilters(empty)
    setStatusChip("")
    fetchDeviceData(1, paginationData.pageSize, empty, "")
  }

  const handleRebootAll = async () => {
    try {
      const res = await apiClient("devices/rebootAll", "POST")
      if (res.success) message.success(res.message)
    } catch {
      message.error("Error rebooting devices")
    }
  }

  const handleReboot = async () => {
    try {
      const res = await apiClient("devices/tasks", "POST", {
        ids: selectedRowKeys,
        command: "reboot",
      })
      if (res.success) {
        message.success(res.message)
        setSelectedRowKeys([])
      }
    } catch {
      message.error("Error rebooting devices")
    }
  }

  const kpis = [
    { key: "", label: "Needs reboot", value: summary.total, tone: "#ea580c", soft: "rgba(234, 88, 12, 0.16)" },
    { key: "Online", label: "Online now", value: summary.online, tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
    { key: "Away", label: "Away", value: summary.away, tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
    { key: "Offline", label: "Offline", value: summary.offline, tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
  ]

  const columns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 280,
      render: (text, record) => {
        const model = resolveDeviceModelImage(record)
        const rebooting = isDeviceRebooting(record.hostname)
        return (
          <div className="inv-device" style={{ opacity: rebooting ? 0.6 : 1 }}>
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    record.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={record}>{text}</HostnameLink>
                {rebooting ? <Spin size="small" /> : null}
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{shortUser(record.user)}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "deviceStatus",
      width: 110,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Last reboot",
      dataIndex: "reboot",
      key: "reboot",
      width: 160,
      render: (text, record) => {
        if (!text) return <span className="ops-muted">Unknown</span>
        const rebootDate = dayjs(text)
        const days = dayjs().diff(rebootDate, "day")
        const color = days < 5 ? "success" : days <= 14 ? "warning" : "error"
        return (
          <div className="reboot-when">
            <Tag color={color}>{rebootDate.fromNow()}</Tag>
            <span className="ops-muted">
              {isDeviceRebooting(record.hostname) ? (
                <>Queuing… <Spin size="small" /></>
              ) : (
                renderDate(text)
              )}
            </span>
          </div>
        )
      },
    },
    {
      title: "Reason",
      key: "reason",
      width: 180,
      ellipsis: true,
      render: (_, record) =>
        record.pendingRebootReason ||
        (record.pendingReboot ? "Pending reboot after updates" : "Requires restart"),
    },
  ]

  return (
    <PanelLayout
      title="Reboot devices"
      subtitle="Queue restarts for endpoints that still owe a reboot after updates."
      actions={
        selectedRowKeys.length === 0 ? (
          <Button
            type="primary"
            disabled={isAnyDeviceRebooting() || !summary.total}
            loading={isAnyDeviceRebooting()}
            icon={<SyncOutlined />}
            onClick={handleRebootAll}
          >
            Reboot all
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleReboot}
            disabled={isAnyDeviceRebooting()}
            loading={isAnyDeviceRebooting()}
          >
            Reboot ({selectedRowKeys.length})
          </Button>
        )
      }
    >
      <div className="ops-kpis">
        {kpis.map((item) => (
          <button
            type="button"
            key={item.key || "all"}
            className={`ops-kpi${statusChip === item.key ? " is-active" : ""}`}
            style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
            onClick={() => {
              setStatusChip(item.key)
              setPaginationData((prev) => ({ ...prev, page: 1 }))
              fetchDeviceData(1, paginationData.pageSize, filters, item.key)
            }}
          >
            <div>
              <span className="ops-kpi-label">{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          </button>
        ))}
      </div>

      {selectedRowKeys.length > 0 ? (
        <div className="inventory-bulk-bar">
          <span>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            type="primary"
            icon={<SyncOutlined />}
            disabled={isAnyDeviceRebooting()}
            loading={isAnyDeviceRebooting()}
            onClick={handleReboot}
          >
            Reboot selected
          </Button>
          <Button size="small" onClick={() => setSelectedRowKeys([])}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {summary.total} device{summary.total === 1 ? "" : "s"} awaiting reboot
        </span>
      </div>

      {devices.length || loading ? (
        <TableBox
          page={page}
          title={null}
          columns={columns}
          data={devices}
          loading={loading}
          className="dashboard-inventory-table"
          rowKey="_id"
          rowSelection={rowSelection}
          pagination={{
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          enableFilter
          filtercontent={
            <div className="p-3">
              <AutoCompleteBox
                label="Hostname"
                name="hostname"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search hostname"
                value={filters.hostname}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, hostname: value }))
                }
              />
              <AutoCompleteBox
                label="User"
                name="user"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search user"
                value={filters.user}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, user: value }))
                }
              />
            </div>
          }
          onFilter={() => {
            setPaginationData((prev) => ({ ...prev, page: 1 }))
            fetchDeviceData(1, paginationData.pageSize, filters, statusChip)
          }}
          onClear={handleClearFilter}
          filters={filters}
        />
      ) : (
        <EmptyState
          title="No reboots owed"
          description="No enrolled devices currently report a pending reboot."
        />
      )}
    </PanelLayout>
  )
}

export default RebootDevices
